// library imports
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Tooltip,
  Modal,
  Divider,
  Flex,
  Spin,
  Select,
  DatePicker,
} from "antd";
import { useEffect, useState, useContext } from "react";
import dayjs from "dayjs";
// component imports
import GoalForm from "./GoalForm";
import DataTable from "../../../components/common/DataTable";
// context imports
import { IndexContext } from "../../../context/IndexContext";
// hook imports
import useFetch from "../../../hooks/useFetch";

export default function GoalView() {
  // index context
  const { siteSelected, setSiteSelected, siteList, getSiteSelectionLoading } =
    useContext(IndexContext);

  // fetch wrapper
  const [fetchWrapper] = useFetch();

  // goal data to be displayed in table
  const [goalDetails, setGoalDetails] = useState([]);

  // goal table loading state
  const [goalTableLoading, setGoalTableLoading] = useState(false);

  // add modal opening state
  const [isAddGoalModalOpen, setIsAddGoalModalOpen] = useState(false);

  // edit modal opening state
  const [isEditGoalModalOpen, setIsEditGoalModalOpen] = useState(false);

  // edit form initial values
  const [formInitialValues, setFormInitialValues] = useState({});

  // selected year
  const [yearSelected, setYearSelected] = useState(dayjs().startOf("year"));

  // selected goal name ->
  // used to know which form item to display in goal form when mode is 'edit'
  // and also know which value to be saved after clicking 'Submit' in goal form 'edit' mode
  const [isEditCarbonEmissionGoal, setIsEditCarbonEmissionGoal] =
    useState(false);
  const [isEditRevenueTarget, setIsEditRevenueTarget] = useState(false);

  const goalTableColumns = [
    {
      title: "Goal",
      dataIndex: "GoalName",
      key: "GoalName",
    },
    {
      title: "Value",
      dataIndex: "GoalValue",
      key: "GoalValue",
    },
    {
      title: "",
      dataIndex: "Action",
      key: "Action",
      width: "11%",
      render: (_, record) => (
        <Flex justify="flex-start" align="center" gap={"5px"} wrap={true}>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined style={{ color: "blue" }} />}
              onClick={() => {
                getGoalFormData(record);
                setIsEditGoalModalOpen(true);
                if (record.GoalName === "Carbon Emission") {
                  setIsEditCarbonEmissionGoal(true);
                  setIsEditRevenueTarget(false);
                }
                if (record.GoalName === "Revenue") {
                  setIsEditCarbonEmissionGoal(false);
                  setIsEditRevenueTarget(true);
                }
              }}
            />
          </Tooltip>
        </Flex>
      ),
    },
  ];

  /* API to get goal form data to initialise form when form is in 'edit' mode */
  function getGoalFormData(row_record) {
    fetchWrapper({
      method: "GET",
      endpoint_url: "goal_management/getGoalFormData",
      set_content_type_header: false,
      params: {
        goal_id: row_record.ID,
      },
      onSuccess: (fetch_output) => {
        setFormInitialValues(fetch_output.data);
      },
    });
  }

  /* API to get goal table data */
  function getGoalDetails() {
    setGoalTableLoading(true);
    fetchWrapper({
      method: "GET",
      endpoint_url: "goal_management/getGoalDetails",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
        year: yearSelected.format("YYYY"),
      },
      onSuccess: (fetch_output) => {
        setGoalTableLoading(false);
        setGoalDetails(fetch_output.data);
      },
    });
  }

  useEffect(() => {
    if (siteSelected) {
      getGoalDetails();
    }
  }, [siteSelected, yearSelected]);

  return (
    <>
      <Flex align="center">
        <p className="mr-2">Site:</p>
        <Select
          style={{ width: "100%", textAlign: "left" }}
          loading={getSiteSelectionLoading}
          showSearch
          value={siteSelected}
          options={siteList}
          optionFilterProp="label"
          labelRender={(value) => <>{value.label}</>}
          onChange={(value) => {
            setSiteSelected(value);
          }}
        />
      </Flex>

      <Flex align="center">
        <span>Year :</span>
        <DatePicker
          picker="year"
          style={{ marginLeft: "4px" }}
          value={yearSelected}
          onChange={(value) => {
            setYearSelected(value.startOf("year"));
          }}
        />
      </Flex>

      <Flex>
        <Button
          icon={<PlusOutlined />}
          className="mt-3 mb-5"
          onClick={() => setIsAddGoalModalOpen(true)}
          disabled={goalDetails.length === 0 ? false : true}
        >
          Goal
        </Button>
      </Flex>

      {goalTableLoading === true ? (
        <Flex justify="center" align="center" style={{ height: "50vh" }}>
          <Spin />
        </Flex>
      ) : (
        <DataTable
          table_column={goalTableColumns}
          table_data={goalDetails}
          scroll_width={1500}
        />
      )}

      <Modal
        title="Add Goal"
        open={isAddGoalModalOpen}
        footer={null}
        closable={false}
        maskClosable={false}
      >
        <Divider className="mb-4 mt-0"></Divider>
        <GoalForm
          mode="add"
          setIsModalOpen={setIsAddGoalModalOpen}
          refreshTable={getGoalDetails}
          siteSelected={siteSelected}
          yearSelected={yearSelected}
        />
      </Modal>

      <Modal
        title="Edit Goal"
        open={isEditGoalModalOpen}
        footer={null}
        closable={false}
        maskClosable={false}
      >
        <Divider className="mb-4 mt-0"></Divider>
        <GoalForm
          mode="edit"
          setIsModalOpen={setIsEditGoalModalOpen}
          refreshTable={getGoalDetails}
          initialValues={formInitialValues}
          isEditCarbonEmissionGoal={isEditCarbonEmissionGoal}
          isEditRevenueTarget={isEditRevenueTarget}
        />
      </Modal>
    </>
  );
}
